import { useEffect, useMemo, useRef, useState } from 'react';

// Searchable single-select dropdown for long option lists (type to filter + scroll).
// options: array of strings, or { value, label }. onChange(value) fires on pick.
// emptyOption: optional label for a top "clear" entry that selects '' (e.g. "All resellers").
// creatable: when true and the typed query doesn't match any option, an "Add …"
// row lets the user pick that raw typed value instead of only existing ones.
export function Combobox({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyOption,
  creatable = false,
}) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery] = useState('');

  // Open the menu, flipping it above the field when there isn't enough room below
  // (e.g. the field sits near the bottom of a modal) so all options stay visible.
  const MENU_SPACE_PX = 320; // approx. popover height (search box + option list)
  const toggle = () => setOpen((v) => {
    const next = !v;
    if (next && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setDropUp(window.innerHeight - r.bottom < MENU_SPACE_PX && r.top > MENU_SPACE_PX);
    }
    return next;
  });

  const opts = useMemo(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  );
  const q = query.trim().toLowerCase();
  const filtered = q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts;
  const current = opts.find((o) => o.value === value);
  // A created value won't be in `options` — fall back to showing it verbatim
  // rather than the placeholder, so a typed-and-picked family stays visible.
  const shownLabel = current ? current.label : (value || emptyOption || placeholder);
  const exactMatch = q && opts.some((o) => o.label.toLowerCase() === q);
  const showCreateRow = creatable && q && !exactMatch;

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); setQuery(''); };

  return (
    <div className="relative" ref={ref}>
      {label && <span className="block mb-1 text-sm font-medium text-slate-600 dark:text-slate-300">{label}</span>}
      <button
        type="button"
        onClick={toggle}
        title={typeof shownLabel === 'string' ? shownLabel : undefined}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-300 dark:border-white/15 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        <span className="truncate">{shownLabel}</span>
        <svg className={`h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.2 7.2a.75.75 0 011.06.02L10 11.06l3.74-3.84a.75.75 0 111.08 1.04l-4.28 4.4a.75.75 0 01-1.08 0l-4.28-4.4a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className={`absolute left-0 right-0 z-30 rounded-xl bg-white dark:bg-slate-800 p-2 shadow-xl ring-1 ring-slate-200 dark:ring-white/10 ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="mb-2 w-full rounded-lg border border-slate-200 dark:border-white/15 bg-slate-50 dark:bg-slate-900/50 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand/25"
          />
          {creatable && !q && (
            <p className="mb-1.5 px-1 text-[11px] text-slate-400 dark:text-slate-500">Pick one below, or type a name to add a new one.</p>
          )}
          <ul className="max-h-52 overflow-y-auto">
            {emptyOption !== undefined && (!q || emptyOption.toLowerCase().includes(q)) && (
              <li>
                <button
                  type="button"
                  onClick={() => pick('')}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${value === '' ? 'bg-brand/10 text-brand-dark dark:bg-brand/20 dark:text-brand-light font-medium' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                >
                  {emptyOption}
                </button>
              </li>
            )}
            {showCreateRow && (
              <li>
                <button
                  type="button"
                  onClick={() => pick(query.trim())}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-brand hover:bg-brand/5 dark:hover:bg-brand/10"
                >
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M10 4v12M4 10h12" /></svg>
                  Add "{query.trim()}"
                </button>
              </li>
            )}
            {filtered.length === 0 && !showCreateRow && (
              <li className="px-3 py-3 text-center text-sm text-slate-400 dark:text-slate-500">No matches</li>
            )}
            {filtered.map((o) => {
              const selected = o.value === value;
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => pick(o.value)}
                    title={o.label}
                    className={`flex w-full items-start justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm ${selected ? 'bg-brand/10 text-brand-dark dark:bg-brand/20 dark:text-brand-light font-medium' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                  >
                    <span className="whitespace-normal break-words">{o.label}</span>
                    {selected && (
                      <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 10l4 4 8-8" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
